import dayjs from 'dayjs';

/**
 * Utility functions for work order operations
 */

// Work order categories for mapping inspection issues
export const workOrderCategories = [
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'body_repair', label: 'Body Repair' },
  { value: 'tire_service', label: 'Tire Service' },
  { value: 'brake_service', label: 'Brake Service' },
  { value: 'engine_service', label: 'Engine Service' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'exhaust', label: 'Exhaust System' },
  { value: 'cooling_system', label: 'Cooling System' },
  { value: 'fuel_system', label: 'Fuel System' },
  { value: 'interior', label: 'Interior' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'other', label: 'Other' }
];

// Inspection checklist items and labels
export const checklistItems = [
  'gauges',
  'warningLights',
  'brakes',
  'exterior',
  'interior',
  'lights',
  'fluids',
  'tires',
];

export const checklistLabels = {
  gauges: 'Gauges',
  warningLights: 'Warning Lights',
  brakes: 'Brakes',
  exterior: 'Exterior',
  interior: 'Interior',
  lights: 'Lights',
  fluids: 'Fluids',
  tires: 'Tires',
};

// Helper function to map inspection items to work order categories
export const mapInspectionToCategory = (inspectionItem) => {
  const mapping = {
    gauges: 'electrical',
    warningLights: 'electrical',
    brakes: 'brake_service',
    exterior: 'exterior',
    interior: 'interior',
    lights: 'electrical',
    fluids: 'mechanical',
    tires: 'tire_service'
  };
  return mapping[inspectionItem] || 'other';
};

/**
 * Check if inspection has any issues (failed or issue items)
 * @param {Object} checklist - The inspection checklist object
 * @returns {boolean} - True if there are issues
 */
export const hasIssues = (checklist) => {
  if (!checklist) return false;
  return Object.values(checklist).some(value => value === 'issue' || value === 'no');
};

/**
 * Get all issues from inspection checklist
 * @param {Object} checklist - The inspection checklist object
 * @param {Object} checklistNotes - The inspection checklist notes object
 * @returns {Array} - Array of issue objects
 */
export const getIssues = (checklist, checklistNotes) => {
  if (!checklist) return [];
  
  const issues = [];
  Object.keys(checklist).forEach(item => {
    if (checklist[item] === 'issue' || checklist[item] === 'no') {
      const issueDetails = checklistNotes?.[item] || (checklist[item] === 'no' ? 'Failed inspection item' : 'Issue found');
      const category = mapInspectionToCategory(item);
      issues.push({
        component: checklistLabels[item],
        details: issueDetails,
        category: category,
        originalKey: item
      });
    }
  });
  
  return issues;
};

/**
 * Generate work order data for bulk creation
 * @param {Array} issues - Array of issue objects
 * @param {Object} formValues - Form values containing inspection data
 * @param {string} inspectionId - The inspection ID
 * @param {string} type - Type of work order creation ('auto' or 'manual')
 * @returns {Array} - Array of work order data objects
 */
export const generateWorkOrderData = (issues, formValues, inspectionId, type = 'auto') => {
  const isAuto = type === 'auto';
  const titlePrefix = isAuto ? 'Auto-Generated Work Order' : 'Failed Inspection';
  const descriptionPrefix = isAuto ? 'Automatically generated work order from failed inspection item' : 'Work order created from failed inspection item';
  const detailsPrefix = isAuto ? 'Auto-Generated from Failed Inspection' : 'Failed Inspection Item';
  
  return issues.map((issue) => ({
    title: `${issue.component} - ${titlePrefix}`,
    description:`${detailsPrefix}\n\nFailed Item: ${issue.component}\nIssue Details: ${issue.details}\nInspection Date: ${formValues.date}\nInspector: ${formValues.inspector}\nComponent: ${issue.component}\n\nThis work order was ${isAuto ? 'automatically created when the inspection was completed with failed items' : 'created to address a failed inspection item'}.`,
    category: issue.category,
    status: 'Open',
    dateOpened: dayjs().format('YYYY-MM-DD'),
    workOrderDetails: "",
    inspection: { connect: { id: inspectionId } },
    workOrders: formValues.vehicleId ? { connect: { id: formValues.vehicleId } } : undefined
  }));
};

/**
 * Create work orders with bulk creation fallback
 * @param {Function} createBulkWorkOrders - Bulk creation mutation function
 * @param {Function} createWorkOrder - Individual creation mutation function
 * @param {Array} workOrdersData - Array of work order data
 * @returns {Promise} - Promise resolving to creation results
 */
export const createWorkOrdersWithFallback = async (createBulkWorkOrders, createWorkOrder, workOrdersData) => {
  try {
    // Try bulk creation first with array of work orders
    const bulkResult = await createBulkWorkOrders({ 
      variables: { data: workOrdersData } 
    });
    
    return {
      success: true,
      method: 'bulk',
      count: bulkResult.data?.workOrderCreateMany?.count || 0,
      data: workOrdersData // Return the original data since bulk only returns count
    };
  } catch (bulkError) {
    console.log('Bulk creation failed, falling back to individual creation:', bulkError);
    
    // Fallback to individual creation
    const workOrderPromises = workOrdersData.map(async (workOrderData) => {
      return createWorkOrder({ variables: { data: workOrderData } });
    });

    const results = await Promise.all(workOrderPromises);
    return {
      success: true,
      method: 'individual',
      count: results.length,
      data: results.map(result => result.data?.workOrderCreate)
    };
  }
};

/**
 * Get new issues by comparing current and original inspection data
 * @param {Object} currentChecklist - Current inspection checklist
 * @param {Object} currentChecklistNotes - Current inspection checklist notes
 * @param {Object} originalChecklist - Original inspection checklist
 * @param {Object} originalChecklistNotes - Original inspection checklist notes
 * @returns {Array} - Array of new or changed issues
 */
export const getNewIssues = (currentChecklist, currentChecklistNotes, originalChecklist, originalChecklistNotes) => {
  const currentIssues = getIssues(currentChecklist || {}, currentChecklistNotes || {});
  const originalIssues = getIssues(originalChecklist || {}, originalChecklistNotes || {});
  
  return currentIssues.filter(currentIssue => {
    // Check if this issue didn't exist in original OR if it was changed
    const originalIssue = originalIssues.find(orig => orig.originalKey === currentIssue.originalKey);
    
    if (!originalIssue) {
      // This is a completely new issue
      return true;
    }
    
    // Check if the details changed
    if (originalIssue.details !== currentIssue.details) {
      return true;
    }
    
    return false;
  });
};
